/**
 * Blog post: telc B2 Prüfung – Aufbau, Zeiten, Punkte, Bestehen, telc vs. Goethe.
 * SEO target (GSC): "telc b2 prüfung", "telc b2 exam", "b2 schriftliche prüfung telc".
 * Facts match the /mock-exams/telc-b2 page.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function TelcB2ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;
  const link = 'font-bold text-black underline underline-offset-2 hover:opacity-80';

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>Von Lehrern entwickelt, abgeglichen mit dem offiziellen telc-Prüfungsaufbau.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Die <strong>telc B2 Prüfung</strong> besteht aus fünf Teilen, dauert insgesamt 2 Stunden 35 Minuten
        und bringt maximal 300 Punkte. Bestanden hast du, wenn du im schriftlichen und im mündlichen Teil
        jeweils mindestens 60 % erreichst. Hier siehst du, was in jedem Teil von dir verlangt wird, wie
        viele Punkte du brauchst und wie du dich gezielt vorbereitest.
      </p>
      <p className="text-base text-black/60 leading-relaxed mb-10">
        <em>In English:</em> the telc Deutsch B2 exam has five parts (reading, language elements, listening,
        written and oral expression), 300 points in total and a 60 % pass mark, applied separately to the
        written and the oral part.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Aufbau der telc B2 Prüfung
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Der schriftliche Teil bringt 225 Punkte, der mündliche Teil 75 Punkte:
      </p>
      <div className="overflow-x-auto my-6 rounded-2xl border-2 border-black/10 bg-white shadow-sm">
        <table className="w-full min-w-[480px] text-left text-sm">
          <thead>
            <tr className="border-b-2 border-black/10 bg-black/5">
              <th className="px-4 py-3 font-space font-bold text-black">Prüfungsteil</th>
              <th className="px-4 py-3 font-space font-bold text-black">Zeit</th>
              <th className="px-4 py-3 font-space font-bold text-black">Punkte</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Leseverstehen</td>
              <td className="px-4 py-3 text-black/70">90 Min. (gemeinsam mit Sprachbausteine)</td>
              <td className="px-4 py-3 text-black/70">75</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Sprachbausteine</td>
              <td className="px-4 py-3 text-black/70">gemeinsame Zeit mit Leseverstehen</td>
              <td className="px-4 py-3 text-black/70">30</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Hörverstehen</td>
              <td className="px-4 py-3 text-black/70">20 Min.</td>
              <td className="px-4 py-3 text-black/70">75</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Schriftlicher Ausdruck</td>
              <td className="px-4 py-3 text-black/70">30 Min.</td>
              <td className="px-4 py-3 text-black/70">45</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 font-medium text-black">Mündlicher Ausdruck</td>
              <td className="px-4 py-3 text-black/70">15 Min.</td>
              <td className="px-4 py-3 text-black/70">75</td>
            </tr>
            <tr className="bg-black/[0.03]">
              <td className="px-4 py-3 font-bold text-black">Gesamt</td>
              <td className="px-4 py-3 font-bold text-black">2 Std. 35 Min.</td>
              <td className="px-4 py-3 font-bold text-black">300</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Wie viele Punkte brauchst du zum Bestehen?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Die Bestehensgrenze liegt bei <strong>60 %</strong>, getrennt für beide Teile:
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-6 ml-2">
        <li><strong>Schriftlich:</strong> 135 von 225 Punkten.</li>
        <li><strong>Mündlich:</strong> 45 von 75 Punkten.</li>
      </ul>
      <div className="my-6 p-4 sm:p-5 rounded-2xl border-l-4 border-amber-500 bg-amber-50/80">
        <p className="text-base md:text-lg text-black/80">
          <strong>Wichtig:</strong> Die Teile werden getrennt bewertet. Eine gute mündliche Prüfung gleicht
          einen zu schwachen schriftlichen Teil nicht aus, und umgekehrt.
        </p>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Der Schriftliche Ausdruck: 30 Minuten, ein Brief
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Im <strong>Schriftlichen Ausdruck</strong> schreibst du einen halbformellen Brief oder eine E-Mail,
        zum Beispiel eine Beschwerde oder eine Anfrage. Du hast 30 Minuten und musst mindestens 150 Wörter
        schreiben. Die Aufgabe enthält vier Leitpunkte, von denen du mindestens drei ausführen solltest. Bewertet werden
        Inhalt, kommunikative Gestaltung und sprachliche Korrektheit.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Den Aufbau, die Bewertung und einen vollständigen Musterbrief findest du im
        {' '}<Link to={`${blogBase}/telc-b2-schreiben-guide`} className={link}>telc B2 Schreiben Guide</Link>.
        Passende Formulierungen stehen in den
        {' '}<Link to={`${blogBase}/telc-b2-redemittel-schreiben`} className={link}>telc B2 Redemittel</Link>{' '}
        (kostenlos als PDF).
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        telc B2 oder Goethe B2?
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-10">
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-3">telc Deutsch B2</h3>
          <p className="text-base text-black/70 leading-relaxed mb-4">
            Eine feste Prüfung an einem Termin: schriftlicher und mündlicher Teil, jeweils mit 60 % zu
            bestehen. Die Aufgaben sind alltags- und berufsnah, im Schreiben typischerweise ein Brief.
          </p>
          <Link to={`/${lang}/mock-exams/telc-b2`} className={link}>
            telc B2 Modelltest →
          </Link>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-3">Goethe-Zertifikat B2</h3>
          <p className="text-base text-black/70 leading-relaxed mb-4">
            Vier Module (Lesen, Hören, Schreiben, Sprechen), die du einzeln ablegen kannst. Praktisch, wenn
            du nur einen Teil wiederholen willst.
          </p>
          <Link to={`/${lang}/mock-exams/goethe-b2`} className={link}>
            Goethe B2 Modelltest →
          </Link>
        </div>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Welche Prüfung dein Arbeitgeber, deine Behörde oder dein Studiengang verlangt, siehst du in den
        jeweiligen Vorgaben. Einen ausführlichen Vergleich findest du in
        {' '}<Link to={`${blogBase}/goethe-vs-telc`} className={link}>Goethe oder telc?</Link>
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        So bereitest du dich vor
      </h2>
      <ol className="list-decimal list-inside space-y-3 text-base md:text-lg text-black/70 leading-relaxed mb-6 ml-2">
        <li>
          <strong>Mach einen kompletten Modelltest unter Zeitdruck.</strong> Der
          {' '}<Link to={`/${lang}/mock-exams/telc-b2`} className={link}>telc B2 Modelltest</Link>{' '}
          folgt dem offiziellen Aufbau, und du siehst am Ende zu jeder Aufgabe die richtige Antwort.
        </li>
        <li>
          <strong>Lass deinen Brief bewerten.</strong> Ohne Rückmeldung merkst du nicht, welche Fehler du
          immer wieder machst. Im Modelltest bekommst du Punkte und Feedback zu deinem Text.
        </li>
        <li>
          <strong>Übe das Sprechen laut.</strong> Die Musterdialoge unten zeigen, wie die mündliche Prüfung abläuft.
        </li>
      </ol>
      <SprechenSamplesCallout provider="telc" level="b2" />

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-[#1e293b] text-white">
        <h2 className="font-space text-xl font-bold text-white mb-3">
          Teste dich mit einem kostenlosen telc B2 Modelltest
        </h2>
        <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
          Lesen, Sprachbausteine, Hören und Schreiben mit echter Prüfungszeit, Punkten pro Teil und einer
          Erklärung zu den Antworten. Der erste Modelltest ist kostenlos.
        </p>
        <Link
          to={`/${lang}/mock-exams/telc-b2`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-space font-bold rounded-xl border-2 border-emerald-500 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Zum telc B2 Modelltest
        </Link>
      </div>

      <RelatedResources provider="telc" level="b2" variant="exam-guide" />

    </article>
  );
}
